import { ORDER_TYPE } from '@exam-management/common/utils';
import {
  FilterOperator,
  useSearchOwnedAndAssignedTasksQuery,
  WorkflowCode,
} from '@exam-management/graphql';
import { useNavigate } from '@exam-management/router';
import {
  type GridEventListener,
  type GridRowParams,
  DataGrid,
  ErrorBox,
  NoRowsOverlay,
} from '@shared/components';

import { useQuestionReviewTaskSearchParams } from '../../hooks';

import { type QuestionReviewTask, columns } from './columns';

export default function QuestionReviewTaskList() {
  const navigate = useNavigate();
  const [questionReviewTaskSearchParams, setQuestionReviewTaskSearchParams] =
    useQuestionReviewTaskSearchParams();
  const { page, pageSize, sort, field, code } = questionReviewTaskSearchParams;

  const {
    data: searchOwnedAndAssignedTasksData,
    loading: isSearchingOwnedAndAssignedTasks,
    error: searchOwnedAndAssignedTasksError,
  } = useSearchOwnedAndAssignedTasksQuery({
    variables: {
      searchOwnedAndAssignedTasksInput: {
        currentWorkflowStepCode: code ? [code] : null,
        workflowCode: WorkflowCode.QuestionReview,
        filter: {
          conditions: [
            {
              field: 'parentId',
              operator: FilterOperator.IsNull,
            },
          ],
        },
        order: {
          field: field,
          type: ORDER_TYPE[sort],
        },
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
      },
    },
  });

  const ownedAndAssignedTasks =
    searchOwnedAndAssignedTasksData?.wflSearchOwnedAndAssignedTasks.tasks ?? [];

  const ownedAndAssignedTasksCount =
    searchOwnedAndAssignedTasksData?.wflSearchOwnedAndAssignedTasks.total ?? 0;

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<QuestionReviewTask>,
  ) => {
    navigate('/task-management/question-review/:taskId', {
      params: {
        taskId: params.row.id,
      },
    });
  };

  if (searchOwnedAndAssignedTasksError) {
    return <ErrorBox />;
  }

  return (
    <DataGrid
      columns={columns}
      getRowId={(row) => row.id}
      rows={ownedAndAssignedTasks}
      loading={isSearchingOwnedAndAssignedTasks}
      rowCount={ownedAndAssignedTasksCount}
      disableColumnFilter
      disableColumnMenu
      hideFooter={isSearchingOwnedAndAssignedTasks || ownedAndAssignedTasksCount === 0}
      rowHeight={48}
      onRowClick={handleRowClick}
      slots={{
        noRowsOverlay: NoRowsOverlay,
      }}
      slotProps={{
        footer: {
          total: ownedAndAssignedTasksCount,
          pagination: {
            page: page,
            count: ownedAndAssignedTasksCount,
            rowsPerPage: pageSize,
            onPageChange: (_event, page) => {
              setQuestionReviewTaskSearchParams({ page });
            },
            onRowsPerPageChange: (event) => {
              setQuestionReviewTaskSearchParams({
                pageSize: parseInt(event.target.value),
                page: 0,
              });
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const { field, sort } = sorting[0] ?? {
          field: questionReviewTaskSearchParams.field,
          sort: questionReviewTaskSearchParams.sort,
        };

        if (sort) {
          setQuestionReviewTaskSearchParams({ field, sort });
        }
      }}
      initialState={{
        sorting: {
          sortModel: [{ field, sort }],
        },
      }}
      sx={(theme) => ({
        '.MuiDataGrid-cell': {
          padding: theme.spacing(3),
          fontSize: theme.typography.body2.fontSize,
        },
        '.MuiDataGrid-columnHeader': {
          padding: theme.spacing(3),
          borderRadius: theme.shape.rounded(0),
        },
        '.MuiDataGrid-row > .MuiDataGrid-withBorderColor': {
          borderColor: theme.palette.grey[200],
        },
        borderRadius: theme.shape.rounded(0),
        '.MuiDataGrid-columnHeaders': {
          borderRadius: theme.shape.rounded(0),
        },
        backgroundColor: theme.palette.background.paper,
        '--DataGrid-overlayHeight': '100%',
        '.MuiDataGrid-overlayWrapper': {
          height: '100%',
        },
      })}
    />
  );
}
